<script>
export default {
  props: {
    closeOnBackdropClick: {
      type: Boolean,
      default: true,
    },
    show: Boolean,
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    modalType: {
      type: String,
      default: 'centered',
    },
    size: {
      type: String,
      default: '',
    },
    // Permite customizar o z-index da modal. Usar valores maiores que 10050
    // para modais que devem aparecer sobre outras modais (ex: confirmação,
    // alerta em modal de gerenciar algo).
    zIndex: {
      type: Number,
      default: 10050,
    },
  },
  data() {
    return {
      mousedDownOnBackdrop: false,
      portalRootEl: null,
    };
  },
  computed: {
    modalClassName() {
      const modalClassNameMap = {
        centered: '',
        'right-aligned': 'right-aligned',
      };

      return `modal-mask skip-context-menu ${
        modalClassNameMap[this.modalType] || ''
      }`;
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.blurActiveElement();
      }
    },
  },
  mounted() {
    this.portalRootEl = this.$refs.portalRoot;
    if (this.portalRootEl && this.portalRootEl.parentNode !== document.body) {
      document.body.appendChild(this.portalRootEl);
    }

    document.addEventListener('keydown', e => {
      if (this.show && e.code === 'Escape') {
        this.onClose();
      }
    });

    document.body.addEventListener('mouseup', this.onMouseUp);
    if (this.show) {
      this.blurActiveElement();
    }
  },
  beforeDestroy() {
    document.body.removeEventListener('mouseup', this.onMouseUp);
    if (this.portalRootEl && this.portalRootEl.parentNode) {
      this.portalRootEl.parentNode.removeChild(this.portalRootEl);
    }
  },
  methods: {
    handleMouseDown() {
      this.mousedDownOnBackdrop = true;
    },
    blurActiveElement() {
      const activeElement = document.activeElement;

      if (activeElement && typeof activeElement.blur === 'function') {
        activeElement.blur();
      }
    },
    close() {
      this.onClose();
    },
    onMouseUp() {
      if (this.mousedDownOnBackdrop) {
        this.mousedDownOnBackdrop = false;
        if (this.closeOnBackdropClick) {
          this.onClose();
        }
      }
    },
  },
};
</script>

<template>
  <div ref="portalRoot">
    <transition name="modal-fade">
      <div
        v-if="show"
        :class="modalClassName"
        :style="{ zIndex: zIndex }"
        transition="modal"
        @mousedown="handleMouseDown"
      >
        <!-- `max-w-[calc(100vw-2rem)]`: a largura era 37.5rem (600px) fixa, o
             que estoura a viewport em qualquer telefone. -->
        <div
          class="relative max-h-full overflow-auto bg-surface-raised border border-line modal-container rtl:text-right skip-context-menu"
          :class="{
            'rounded-lg w-[37.5rem] max-w-[calc(100vw-2rem)]': !fullWidth,
            'items-center rounded-none flex h-full justify-center w-full':
              fullWidth,
            [size]: true,
          }"
          @mouse.stop
          @mousedown="event => event.stopPropagation()"
        >
          <woot-button
            v-if="showCloseButton"
            color-scheme="secondary"
            icon="dismiss"
            variant="clear"
            class="absolute right-2 top-2 z-10"
            @click="close"
          />
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss">
.modal-mask {
  @apply flex items-center justify-center bg-modal-backdrop-light dark:bg-modal-backdrop-dark z-[10050] h-full left-0 fixed top-0 w-full;
  .modal-container {
    &.medium {
      @apply max-w-[80%] w-[56.25rem];
    }
    &.modal-big {
      @apply w-full;
    }
    &.whatsapp-template-modal {
      @apply max-w-[90vw] w-[72rem];
    }
    // .content-box {
    //   @apply h-auto p-0;
    // }
    .content {
      @apply p-8;
    }
    form,
    .modal-content {
      @apply pt-4 pb-8 px-8 self-center;
      a {
        @apply p-4;
      }
    }

    .condition-group-modal__content {
      max-height: calc(100vh - 12rem);
      overflow-y: auto;
      overscroll-behavior: contain;
    }
  }
}
.modal-mask.right-aligned {
  @apply justify-end;
  .modal-container {
    @apply rounded-none h-full w-[30rem];
  }
}
// O <transition> se chama `modal-fade`, entao o Vue procura
// `.modal-fade-enter`, `.modal-fade-enter-active`, `.modal-fade-leave-to`...
// As regras aqui eram `.modal-enter` e `.modal-leave`, de um transition
// chamado `modal` -- nomes que nao existem. Resultado: o modal nunca animou,
// aparecia e desaparecia seco. `.modal-leave` tambem estava errado por si:
// no Vue 2 o estado final de saida e `-leave-to`.
.modal-fade-enter-active,
.modal-fade-leave-active {
  @apply transition-opacity duration-200 ease-out;

  .modal-container {
    @apply transition-transform duration-200 ease-out;
  }
}

.modal-fade-enter,
.modal-fade-leave-to {
  @apply opacity-0;

  .modal-container {
    // Entra crescendo de baixo, nao encolhendo de cima: o movimento acompanha
    // a atencao em vez de brigar com ela.
    transform: scale(0.96) translateY(0.5rem);
  }
}

// Quem pede menos movimento ve so o fade, sem o crescer/subir do container.
@media (prefers-reduced-motion: reduce) {
  .modal-fade-enter,
  .modal-fade-leave-to {
    .modal-container {
      transform: none;
    }
  }
}
</style>
